import PersonAddIcon from "@mui/icons-material/PersonAdd";
import { Box, Button, Paper, Tab, Tabs } from "@mui/material";
import { PimoReactComponent } from "@pimo/pimo-app-builder";
import { getLocalizedMessage, SupportedLanguage } from "@pimo/pimo-l10n";
import { useState } from "react";

import {
  App,
  BillingAddress,
  Client,
  Invoice,
  MfaPolicy,
  Plan,
  SeatInfo,
  Subscription,
  User,
  USER_ROLE,
} from "../types/entities";
import { Profile } from "../types/profile";
import { AddUserModal } from "./add-user-modal";
import {
  DeleteAppQueueDialog,
  DeleteAppQueueDialogState,
} from "./delete-app-queue-dialog";
import { PageHeader } from "./page-header";
import {
  PastDuePaymentBanner,
  PastDuePaymentBannerEvent,
} from "./past-due-payment-banner";
import { PricingTab } from "./pricing-tab";
import { ReportsTable } from "./reports-table";
import { RoleChangeDialog } from "./role-change-dialog";
import { TransferOwnershipDialog } from "./transfer-ownership-dialog";
import {
  UpdatePaymentMethodDialog,
  UpdatePaymentMethodDialogEvent,
  UpdatePaymentMethodDialogPayload,
} from "./update-payment-method-dialog";
import { UsersTable } from "./users-table";

export type HomePortalComponentState = {
  apps: App[];
  client?: Client;
  clients?: Client[];
  users?: User[];
  userProfile?: Profile;
  showRoleChangeConfirmDialog?: boolean;
  pendingRoleChangeUserId?: string;
  showTransferOwnershipConfirmDialog?: boolean;
  pendingTransferOwnershipUserId?: string;
  isSwitchingClient?: boolean;
  plans?: Plan[];
  subscription?: Subscription | null;
  seatInfo?: SeatInfo | null;
  billingAddress?: BillingAddress | null;
  /** Invoice history (`GET /subscriptions/invoices`, #2791/#2794) — fetched
   * for both the Owner and an Admin (read-only), see `PricingTab`. */
  invoices?: Invoice[];
  mfaPolicy?: MfaPolicy;
  deleteQueueDialog?: Omit<DeleteAppQueueDialogState, "language">;
  /** Set once the Owner has confirmed a replacement card but the local
   * subscription has not yet moved back to `active` (webhook still
   * pending) — see `PastDuePaymentBanner`/`home-portal-plugin.ts`. */
  isPaymentMethodRetryPending?: boolean;
  updatePaymentMethodDialog?: {
    isOpen: boolean;
    isSubmitting?: boolean;
    errorMessage?: string;
    clientSecret?: string;
  };
  language: SupportedLanguage;
};

export type HomePortalComponentEvent =
  | "create-report"
  | "open-report"
  | "delete-report"
  | "logout"
  | "invite-users"
  | "delete-user"
  | "change-role"
  | "confirm-role-change"
  | "cancel-role-change"
  | "transfer-ownership"
  | "confirm-transfer-ownership"
  | "cancel-transfer-ownership"
  | "refresh-users"
  | "switch-client"
  | "activate-tool"
  | "open-select-plan-modal"
  | "open-change-plan-modal"
  | "edit-billing-address"
  | "open-cancel-subscription-dialog"
  | "navigate-admin"
  | "change-language"
  | "open-update-payment-method-dialog"
  | UpdatePaymentMethodDialogEvent;

export type HomePortalComponentEventPayload =
  | {
      language?: SupportedLanguage;
    }
  | UpdatePaymentMethodDialogPayload;

export const HomePortalPage: PimoReactComponent<
  HomePortalComponentState,
  HomePortalComponentEvent,
  unknown
> = ({
  fireEvent,
  apps = [],
  client,
  clients = [],
  users = [],
  userProfile,
  showRoleChangeConfirmDialog = false,
  pendingRoleChangeUserId,
  showTransferOwnershipConfirmDialog = false,
  pendingTransferOwnershipUserId,
  isSwitchingClient = false,
  plans = [],
  subscription,
  seatInfo,
  billingAddress,
  invoices = [],
  mfaPolicy,
  deleteQueueDialog,
  isPaymentMethodRetryPending,
  updatePaymentMethodDialog,
  language,
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);

  const isCurrentUserAdmin = userProfile?.role === USER_ROLE.ADMIN;
  const isCurrentUserOwner = userProfile?.role === USER_ROLE.OWNER;
  const isCurrentUserAdminOrOwner = isCurrentUserAdmin || isCurrentUserOwner;

  // Redirect off role-restricted tabs: Users and Pricing both need
  // Admin-or-Owner — the Pricing/Billing tab stays visible to an Admin in
  // a read-only state (#2794's UX answer superseding the earlier
  // Owner-only decision); `isOwner={isCurrentUserOwner}` passed to
  // `PricingTab` below is what actually hides its action buttons for an
  // Admin, not tab visibility.
  if (
    (activeTab === 1 && !isCurrentUserAdminOrOwner) ||
    (activeTab === 2 && !isCurrentUserAdminOrOwner)
  ) {
    setActiveTab(0);
  }

  const handleOpenReport = (appId: string) => {
    const app = apps.find((a) => a.id === appId);
    if (app?.url) {
      window.open(app.url, "_blank");
    } else {
      fireEvent?.("open-report", { appId });
    }
  };

  const pendingTransferUser = users.find(
    (u) => u.id === pendingTransferOwnershipUserId
  );
  const pendingTransferUserName = pendingTransferUser
    ? pendingTransferUser.firstName && pendingTransferUser.lastName
      ? `${pendingTransferUser.firstName} ${pendingTransferUser.lastName}`
      : pendingTransferUser.email
    : undefined;

  /**
   * Same event/payload-bridging convention as `select-plan-modal.tsx`'s
   * `fireCheckoutEvent`: `PastDuePaymentBanner` and
   * `UpdatePaymentMethodDialog` declare their own, narrower event/payload
   * types so each stays independently testable, and both are subsets of
   * `HomePortalComponentEvent`/`HomePortalComponentEventPayload`.
   */
  const firePastDueBannerEvent = (event: PastDuePaymentBannerEvent) =>
    fireEvent?.(event);
  const fireUpdatePaymentMethodDialogEvent = (
    event: UpdatePaymentMethodDialogEvent,
    payload?: UpdatePaymentMethodDialogPayload
  ) => fireEvent?.(event, payload);

  return (
    <Box
      sx={{
        height: "100vh",
        bgcolor: "#e4e9fa",
        margin: "-8px",
        padding: "20px",
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        overflow: "hidden",
      }}
    >
      {/* Pimo Logo */}
      <Box
        sx={{
          position: "absolute",
          top: 20,
          left: 20,
          display: "flex",
          alignItems: "center",
        }}
      >
        <img
          src="/pimo-large.svg"
          alt={getLocalizedMessage(language, "homePortalPage.pimoLogoAlt")}
          style={{
            height: 45,
            width: 125,
            display: "block",
            objectFit: "contain",
          }}
        />
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "calc(100vh - 40px)",
        }}
      >
        <Paper
          elevation={0}
          sx={{
            width: "100%",
            maxWidth: "1200px",
            height: "80vh",
            minHeight: "560px",
            maxHeight: "820px",
            borderRadius: "20px",
            boxShadow: "0 2px 16px rgba(0,0,0,0.06)",
            bgcolor: "#fff",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <PageHeader
            client={client}
            clients={clients}
            userProfile={userProfile}
            onSwitchClient={(clientId) =>
              fireEvent?.("switch-client", { clientId })
            }
            onLogout={() => fireEvent?.("logout", {})}
            onOpenAdmin={() => fireEvent?.("navigate-admin", undefined)}
            onChangeLanguage={(language) =>
              fireEvent?.("change-language", { language })
            }
            language={language}
          />

          {/* Navigation Tabs */}
          <Box sx={{ px: 3, pt: 2 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Tabs
                value={activeTab}
                onChange={(_, v: number) => setActiveTab(v)}
                sx={{
                  "& .MuiTab-root": {
                    textTransform: "none",
                    fontWeight: 500,
                    fontSize: "1rem",
                    color: "#666",
                    "&.Mui-selected": { color: "#333" },
                  },
                  "& .MuiTabs-indicator": {
                    backgroundColor: "#3ec6ff",
                    height: 3,
                  },
                }}
              >
                <Tab
                  label={getLocalizedMessage(
                    language,
                    "homePortalPage.tabs.reports.title"
                  )}
                />
                {isCurrentUserAdminOrOwner && (
                  <Tab
                    label={getLocalizedMessage(
                      language,
                      "homePortalPage.tabs.users.title"
                    )}
                  />
                )}
                {isCurrentUserAdminOrOwner && (
                  <Tab
                    label={getLocalizedMessage(
                      language,
                      "homePortalPage.tabs.pricePlans.title"
                    )}
                  />
                )}
              </Tabs>

              {(activeTab === 0 ||
                (activeTab === 2 && isCurrentUserOwner && !subscription)) && (
                <Button
                  variant="contained"
                  onClick={() =>
                    fireEvent?.(
                      activeTab === 0
                        ? "create-report"
                        : "open-select-plan-modal",
                      {}
                    )
                  }
                  sx={{
                    bgcolor: "#ff8c00",
                    color: "#fff",
                    borderRadius: 2,
                    px: 3,
                    py: 1,
                    fontSize: "0.9rem",
                    fontWeight: 500,
                    textTransform: "none",
                    boxShadow: "none",
                    "&:hover": { bgcolor: "#e67e00" },
                  }}
                >
                  {getLocalizedMessage(
                    language,
                    activeTab === 0
                      ? "homePortalPage.actions.createTool"
                      : "homePortalPage.tabs.pricePlans.actions.selectPlan"
                  )}
                </Button>
              )}

              {activeTab === 1 && isCurrentUserAdminOrOwner && (
                <Button
                  variant="contained"
                  startIcon={<PersonAddIcon />}
                  onClick={() => {
                    fireEvent?.("refresh-users");
                    setIsAddUserModalOpen(true);
                  }}
                  sx={{
                    bgcolor: "#ff8c00",
                    color: "#fff",
                    borderRadius: 2,
                    px: 3,
                    py: 1,
                    fontSize: "0.9rem",
                    fontWeight: 500,
                    textTransform: "none",
                    boxShadow: "none",
                    "&:hover": { bgcolor: "#e67e00" },
                  }}
                >
                  {getLocalizedMessage(
                    language,
                    "homePortalPage.actions.addUser"
                  )}
                </Button>
              )}

              {activeTab === 2 && isCurrentUserOwner && subscription && (
                // Disabled while an upgrade awaits its webhook or a cancel is scheduled: a second change would add a real Stripe proration charge.
                <Button
                  variant="contained"
                  disabled={
                    subscription.status === "pending_change" ||
                    subscription.status === "cancel_scheduled"
                  }
                  onClick={() => fireEvent?.("open-change-plan-modal", {})}
                  sx={{
                    bgcolor: "#ff8c00",
                    color: "#fff",
                    borderRadius: 2,
                    px: 3,
                    py: 1,
                    fontSize: "0.9rem",
                    fontWeight: 500,
                    textTransform: "none",
                    boxShadow: "none",
                    "&:hover": { bgcolor: "#e67e00" },
                    "&.Mui-disabled": { bgcolor: "#f0d4b3", color: "#fff" },
                  }}
                >
                  {getLocalizedMessage(
                    language,
                    "homePortalPage.tabs.pricePlans.actions.changePlan"
                  )}
                </Button>
              )}
            </Box>
          </Box>

          {/* Past-due payment banner (#2795): global, not nested inside any
              one tab, so the Owner sees it regardless of which tab they're
              on. Only rendered for Owners — its only action is
              "update payment method", an Owner-only mutating action
              (#2794's UX answer keeps the Pricing tab itself visible to an
              Admin in a read-only state, but this banner is purely an
              action trigger, not informational content, so it stays
              Owner-only rather than moving into that read-only surface). */}
          {isCurrentUserOwner && subscription?.status === "past_due" && (
            <Box sx={{ px: 3, pt: 2 }}>
              <PastDuePaymentBanner
                fireEvent={firePastDueBannerEvent}
                isVisible
                isRetryPending={isPaymentMethodRetryPending}
                language={language}
              />
            </Box>
          )}

          {/* Tab Content */}
          <Box sx={{ p: 5, flex: 1, overflow: "auto", minHeight: 0 }}>
            {activeTab === 0 && (
              <ReportsTable
                apps={apps}
                plans={plans}
                subscription={subscription}
                isSwitchingClient={isSwitchingClient}
                isCurrentUserOwner={isCurrentUserOwner}
                isCurrentUserAdminOrOwner={isCurrentUserAdminOrOwner}
                onOpenReport={handleOpenReport}
                onActivateTool={(appId) =>
                  fireEvent?.("activate-tool", { appId })
                }
                onOpenSelectPlan={() =>
                  fireEvent?.("open-select-plan-modal", {})
                }
                onDelete={(reportId) =>
                  fireEvent?.("delete-report", { reportId })
                }
                language={language}
              />
            )}

            {activeTab === 1 && isCurrentUserAdminOrOwner && (
              <UsersTable
                users={users}
                currentUserEmail={userProfile?.email}
                isCurrentUserAdmin={isCurrentUserAdmin}
                isCurrentUserOwner={isCurrentUserOwner}
                isSwitchingClient={isSwitchingClient}
                mfaPolicy={mfaPolicy}
                onDeleteUser={(userId) =>
                  fireEvent?.("delete-user", { userId })
                }
                onChangeRole={(userId) =>
                  fireEvent?.("change-role", { userId })
                }
                onTransferOwnership={(userId) =>
                  fireEvent?.("transfer-ownership", { userId })
                }
                language={language}
              />
            )}

            {activeTab === 2 && isCurrentUserAdminOrOwner && (
              <PricingTab
                plans={plans}
                subscription={subscription ?? null}
                apps={apps}
                totalUsers={users.length}
                billingAddress={billingAddress ?? null}
                invoices={invoices}
                isOwner={isCurrentUserOwner}
                fireEvent={(event) => {
                  if (event === "edit-billing-address") {
                    fireEvent?.("edit-billing-address", {});
                  } else if (event === "open-cancel-subscription-dialog") {
                    fireEvent?.("open-cancel-subscription-dialog", {});
                  } else if (event === "open-update-payment-method-dialog") {
                    fireEvent?.("open-update-payment-method-dialog", {});
                  }
                }}
                language={language}
              />
            )}
          </Box>
        </Paper>
      </Box>

      <AddUserModal
        isOpen={isAddUserModalOpen}
        users={users}
        currentUserEmail={userProfile?.email}
        seatInfo={seatInfo}
        isCurrentUserOwner={isCurrentUserOwner}
        language={language}
        fireEvent={(event, payload) => {
          if (event === "close-modal") {
            setIsAddUserModalOpen(false);
          } else if (event === "invite-users" && payload) {
            fireEvent?.("invite-users", payload);
          }
        }}
      />

      <RoleChangeDialog
        open={showRoleChangeConfirmDialog}
        onConfirm={() => {
          if (pendingRoleChangeUserId) {
            fireEvent?.("confirm-role-change", {
              userId: pendingRoleChangeUserId,
            });
          }
        }}
        onCancel={() => fireEvent?.("cancel-role-change", {})}
      />

      <TransferOwnershipDialog
        open={showTransferOwnershipConfirmDialog}
        userName={pendingTransferUserName}
        onConfirm={() => {
          if (pendingTransferOwnershipUserId) {
            fireEvent?.("confirm-transfer-ownership", {
              userId: pendingTransferOwnershipUserId,
            });
          }
        }}
        onCancel={() => fireEvent?.("cancel-transfer-ownership", {})}
      />

      <DeleteAppQueueDialog
        isOpen={deleteQueueDialog?.isOpen ?? false}
        processed={deleteQueueDialog?.processed ?? 0}
        total={deleteQueueDialog?.total ?? 0}
        failed={deleteQueueDialog?.failed ?? 0}
        activeAppName={deleteQueueDialog?.activeAppName}
        language={language}
      />

      <UpdatePaymentMethodDialog
        fireEvent={fireUpdatePaymentMethodDialogEvent}
        isOpen={updatePaymentMethodDialog?.isOpen ?? false}
        clientSecret={updatePaymentMethodDialog?.clientSecret}
        isProcessing={updatePaymentMethodDialog?.isSubmitting}
        errorMessage={updatePaymentMethodDialog?.errorMessage}
        language={language}
      />
    </Box>
  );
};
